/**
 * Utility functions for handling async operations with timeouts and error handling
 */

export class TimeoutError extends Error {
  constructor(timeoutMs: number, operation: string = 'Operation') {
    super(`${operation} timed out after ${timeoutMs}ms`);
    this.name = 'TimeoutError';
  }
}

/**
 * Wraps a promise with a timeout
 * @param promise The promise to wrap
 * @param timeoutMs Timeout in milliseconds
 * @param operation Optional operation name for better error messages
 * @returns Promise that resolves with the original promise or rejects with TimeoutError
 */
export const withTimeout = <T>(
  promise: Promise<T>, 
  timeoutMs: number, 
  operation?: string
): Promise<T> => {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) => 
      setTimeout(() => reject(new TimeoutError(timeoutMs, operation)), timeoutMs)
    )
  ]);
};

/**
 * Supabase query result type
 */
export interface SupabaseResult<T> {
  data: T | null;
  error: any;
}

/**
 * Wraps an async function call with proper error typing
 * @param asyncFn The async function to call
 * @param timeoutMs Timeout in milliseconds
 * @param operation Optional operation name for better error messages
 * @returns Promise that resolves with the function result or rejects with TimeoutError
 */
export const withTimeoutTyped = async <T>(
  asyncFn: () => Promise<T>,
  timeoutMs: number,
  operation?: string
): Promise<T> => {
  return withTimeout(asyncFn(), timeoutMs, operation);
};

/**
 * Wraps a Supabase query with timeout and proper typing
 * @param queryFn The Supabase query function to call
 * @param timeoutMs Timeout in milliseconds
 * @param operation Optional operation name for better error messages
 * @returns Promise that resolves with Supabase result structure
 */
export const withSupabaseTimeout = async <T>(
  queryFn: () => Promise<SupabaseResult<T>>,
  timeoutMs: number,
  operation?: string
): Promise<SupabaseResult<T>> => {
  return withTimeout(queryFn(), timeoutMs, operation);
};

/**
 * Wraps an async function with retry logic and timeout
 * @param fn The async function to wrap
 * @param maxRetries Maximum number of retries
 * @param timeoutMs Timeout for each attempt
 * @param delayMs Delay between retries
 * @returns Promise that resolves or rejects after all retries
 */
export const withRetryAndTimeout = async <T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  timeoutMs: number = 10000,
  delayMs: number = 1000
): Promise<T> => {
  let lastError: Error;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await withTimeout(fn(), timeoutMs, `Attempt ${attempt + 1}`);
    } catch (error) {
      lastError = error as Error;
      
      // Don't retry on timeout errors - they're likely persistent
      if (error instanceof TimeoutError) {
        throw error;
      }
      
      // If this was the last attempt, throw the error
      if (attempt === maxRetries) {
        throw lastError;
      }
      
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, delayMs));
    }
  }
  
  throw lastError!;
};

/**
 * Creates an AbortController that automatically aborts after a timeout
 * @param timeoutMs Timeout in milliseconds
 * @returns Object with controller and cleanup function
 */
export const createTimeoutController = (timeoutMs: number) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  
  const cleanup = () => clearTimeout(timeoutId);
  
  return { controller, cleanup };
};

/**
 * Debug logging utility for specific users
 * @param email User email to log for
 * @param operation Operation being performed
 * @param data Additional data to log
 */
export const debugLogForUser = (email: string, operation: string, data?: any) => {
  const targetEmails = ['amarildo121@gmail.com']; // Add more emails as needed
  
  if (targetEmails.includes(email.toLowerCase())) {
    console.log(`[DEBUG-${email}] ${operation}:`, {
      timestamp: new Date().toISOString(),
      operation,
      data
    });
  }
};

/**
 * Safe async operation wrapper that never throws and provides fallback
 * @param operation The async operation to perform
 * @param fallback Fallback value if operation fails
 * @param timeoutMs Timeout in milliseconds
 * @returns Promise that always resolves with either result or fallback
 */
export const safeAsync = async <T>(
  operation: () => Promise<T>,
  fallback: T,
  timeoutMs: number = 10000
): Promise<T> => {
  try {
    return await withTimeout(operation(), timeoutMs);
  } catch (error) {
    console.error('Safe async operation failed:', error);
    return fallback;
  }
};